import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Linkedin, MessageCircle } from "lucide-react";
import logo from "@/assets/kultmans-logo.png";
import { NewsletterForm } from "./NewsletterForm";

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61553922263765", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/kultmans_doebeln/", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@Kultmans", label: "YouTube" },
  { icon: Linkedin, href: "https://www.linkedin.com/in/rico-kretschel-15523829/", label: "LinkedIn" },
  { icon: MessageCircle, href: "https://www.tiktok.com/@kultmans", label: "Twitter" },
];

const footerLinks = [
  {
    title: "Navigation",
    links: [
      { title: "Start", href: "/" },
      { title: "Der Verein", href: "/verein" },
      { title: "Philosophie", href: "/philosophie" },
      { title: "Eventlocation", href: "/eventlocation" },
    ],
  },
  {
    title: "Angebote",
    links: [
      { title: "Kurse & Workshops", href: "/angebote" },
      { title: "Veranstaltungen", href: "/veranstaltungen" },
      { title: "Think-Cube", href: "/ki-think-cube" },
      { title: "Mitglied werden", href: "/mitglied-werden" },
      { title: "Sponsoren & Förderer", href: "/sponsoren" },
    ],
  },
  {
    title: "Kontakt & Rechtliches",
    links: [
      { title: "Kontakt", href: "/kontakt" },
      { title: "Jobs", href: "/jobs" },
      { title: "Presse & Downloads", href: "/presse" },
      { title: "Impressum", href: "/impressum" },
      { title: "Datenschutz", href: "/datenschutz" },
      { title: "Barrierefreiheit", href: "/barrierefreiheit" },
    ],
  },
];

export function Footer() {
  return (
    <>
      {/* Newsletter Section – eigenständiger Bereich über dem Footer */}
      <section className="bg-primary/5 border-y border-primary/15">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div id="newsletter" className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center max-w-4xl mx-auto rounded-2xl bg-card border border-border p-5 sm:p-8 md:p-12 transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_0_40px_hsl(35_80%_55%/0.25)] hover:border-primary/50">
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-3">
                Nichts verpassen!
              </h3>
              <p className="text-muted-foreground">
                Erhalte Neuigkeiten zu Veranstaltungen, Kursen und allem rund um die Kulturmanufaktur direkt in dein Postfach.
              </p>
            </div>
            <NewsletterForm />
          </div>
        </div>
      </section>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-10 md:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-12">
            {/* Brand */}
            <div className="lg:col-span-2">
              <Link to="/" className="inline-block mb-4">
                <img src={logo} alt="Kultmans" width={800} height={184} loading="lazy" decoding="async" className="h-16 w-auto" />
              </Link>
              <p className="text-muted-foreground text-sm mb-6 max-w-sm">
                Kulturmanufaktur im alten Stemke – Ein Ort für Kultur, Bildung, Handwerk und Gemeinschaft in Döbeln.
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    aria-label={social.label}
                  >
                    <social.icon size={18} />
                  </a>
                ))}
              </div>
            </div>

            {/* Links */}
            {footerLinks.map((column) => (
              <div key={column.title}>
                <h4 className="font-display font-semibold text-foreground mb-4">{column.title}</h4>
                <ul className="space-y-2">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <Link to={link.href} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* KI-Hinweis */}
          <p className="mt-10 text-xs text-muted-foreground leading-relaxed max-w-3xl">
            <span className="font-medium">KI-Hinweis:</span> Einzelne Inhalte dieser Website wurden mit Unterstützung von Künstlicher Intelligenz erstellt und vor der Veröffentlichung sorgfältig redaktionell geprüft und freigegeben.
          </p>

          {/* Bottom */}
          <div className="mt-8 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {new Date().getFullYear()} Kultmans e.V. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
              <p className="text-center text-sm text-muted-foreground">Kulturmanufaktur im alten Stemke, Döbeln</p>
              <Link to="/intern" className="text-xs text-muted-foreground hover:text-muted-foreground transition-colors">
                Intern
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
